// src/services/wordpressApi.ts

export interface WordPressPost {
    id: number;
    date: string;
    date_gmt: string;
    guid: {
        rendered: string;
    };
    modified: string;
    modified_gmt: string;
    slug: string;
    status: string;
    type: string;
    link: string;
    title: {
        rendered: string;
    };
    content: {
        rendered: string;
        protected: boolean;
    };
    excerpt: {
        rendered: string;
        protected: boolean;
    };
    author: number;
    featured_media: number;
    comment_status: string;
    ping_status: string;
    sticky: boolean;
    template: string;
    format: string;
    meta: any;
    categories: number[];
    tags: number[];
    _links: any;
}

export interface WordPressMedia {
    id: number;
    date: string;
    slug: string;
    type: string;
    link: string;
    title: {
        rendered: string;
    };
    author: number;
    media_type: string;
    mime_type: string;
    media_details: {
        width: number;
        height: number;
        file: string;
        sizes: {
            [key: string]: {
                file: string;
                width: number;
                height: number;
                mime_type: string;
                source_url: string;
            };
        };
    };
    source_url: string;
}

export interface WordPressCategory {
    id: number;
    count: number;
    description: string;
    link: string;
    name: string;
    slug: string;
    taxonomy: string;
    parent: number;
}

export interface WordPressTag {
    id: number;
    count: number;
    description: string;
    link: string;
    name: string;
    slug: string;
    taxonomy: string;
}

export interface WordPressAuthor {
    id: number;
    name: string;
    url: string;
    description: string;
    link: string;
    slug: string;
    avatar_urls: {
        [key: string]: string;
    };
    meta: any;
    _links: any;
}

export interface WordPressApiResponse<T> {
    data: T;
    headers: {
        'X-WP-Total': string;
        'X-WP-TotalPages': string;
    };
}

class WordPressApiService {
    private baseUrl: string;
    private cache: Map<string, { data: any; timestamp: number }>;
    private cacheTimeout: number = 5 * 60 * 1000; // 5 minutes

    constructor() {
        this.baseUrl = 'https://gmnbckend.gene6.ga/wp-json/wp/v2';
        this.cache = new Map();
    }

    // Méthode privée pour gérer le cache
    private getCachedData(key: string): any | null {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
            return cached.data;
        }
        return null;
    }

    private setCachedData(key: string, data: any): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }

    // Méthode privée pour faire les requêtes avec gestion d'erreur
    private async makeRequest<T>(url: string, useCache: boolean = true): Promise<T> {
        const cacheKey = url;

        // Vérifier le cache
        if (useCache) {
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) {
                return cachedData;
            }
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status} - ${response.statusText}`);
            }

            const data = await response.json();

            // Mettre en cache si demandé
            if (useCache) {
                this.setCachedData(cacheKey, data);
            }

            return data;
        } catch (error) {
            console.error(`Erreur lors de la requête vers ${url}:`, error);
            throw error;
        }
    }

    // Récupérer les articles avec support complet
    async getPosts(params?: {
        per_page?: number;
        page?: number;
        categories?: string;
        tags?: string;
        search?: string;
        orderby?: 'date' | 'title' | 'menu_order' | 'modified' | 'slug';
        order?: 'asc' | 'desc';
        status?: 'publish' | 'draft' | 'private';
        author?: number;
        exclude?: number[];
        include?: number[];
        offset?: number;
        before?: string;
        after?: string;
        slug?: string;
        sticky?: boolean;
    }): Promise<WordPressPost[]> {
        const queryParams = new URLSearchParams();

        // Valeurs par défaut
        queryParams.append('status', 'publish');
        queryParams.append('per_page', '10');
        queryParams.append('orderby', 'date');
        queryParams.append('order', 'desc');

        if (params) {
            if (params.per_page) queryParams.set('per_page', params.per_page.toString());
            if (params.page) queryParams.append('page', params.page.toString());
            if (params.categories) queryParams.append('categories', params.categories);
            if (params.tags) queryParams.append('tags', params.tags);
            if (params.search) queryParams.append('search', params.search);
            if (params.orderby) queryParams.set('orderby', params.orderby);
            if (params.order) queryParams.set('order', params.order);
            if (params.status) queryParams.set('status', params.status);
            if (params.author) queryParams.append('author', params.author.toString());
            if (params.exclude && params.exclude.length > 0) {
                queryParams.append('exclude', params.exclude.join(','));
            }
            if (params.include && params.include.length > 0) {
                queryParams.append('include', params.include.join(','));
            }
            if (params.offset) queryParams.append('offset', params.offset.toString());
            if (params.before) queryParams.append('before', params.before);
            if (params.after) queryParams.append('after', params.after);
            if (params.slug) queryParams.append('slug', params.slug);
            if (params.sticky !== undefined) queryParams.append('sticky', params.sticky.toString());
        }

        const url = `${this.baseUrl}/posts?${queryParams.toString()}`;
        return this.makeRequest<WordPressPost[]>(url);
    }

    // Récupérer un article spécifique par ID
    async getPost(id: number): Promise<WordPressPost> {
        const url = `${this.baseUrl}/posts/${id}`;
        return this.makeRequest<WordPressPost>(url);
    }

    // Récupérer un article par slug
    async getPostBySlug(slug: string): Promise<WordPressPost | null> {
        try {
            const posts = await this.getPosts({ slug, per_page: 1 });
            return posts.length > 0 ? posts[0] : null;
        } catch (error) {
            console.error('Erreur lors de la récupération de l\'article par slug:', error);
            throw error;
        }
    }

    // Récupérer les articles liés (même catégorie)
    async getRelatedPosts(postId: number, categoryIds: number[], limit: number = 3): Promise<WordPressPost[]> {
        try {
            if (categoryIds.length === 0) return [];

            const posts = await this.getPosts({
                categories: categoryIds.join(','),
                exclude: [postId],
                per_page: limit,
                orderby: 'date',
                order: 'desc'
            });

            return posts;
        } catch (error) {
            console.error('Erreur lors de la récupération des articles liés:', error);
            return [];
        }
    }

    // Récupérer les articles populaires/sticky
    async getStickyPosts(limit: number = 5): Promise<WordPressPost[]> {
        try {
            return await this.getPosts({
                sticky: true,
                per_page: limit,
                orderby: 'date',
                order: 'desc'
            });
        } catch (error) {
            console.error('Erreur lors de la récupération des articles épinglés:', error);
            return [];
        }
    }

    // Récupérer les médias (images)
    async getMedia(id: number): Promise<WordPressMedia> {
        const url = `${this.baseUrl}/media/${id}`;
        return this.makeRequest<WordPressMedia>(url);
    }

    // Récupérer plusieurs médias
    async getMultipleMedia(ids: number[]): Promise<WordPressMedia[]> {
        if (ids.length === 0) return [];

        const url = `${this.baseUrl}/media?include=${ids.join(',')}`;
        return this.makeRequest<WordPressMedia[]>(url);
    }

    // Récupérer les catégories
    async getCategories(params?: {
        per_page?: number;
        page?: number;
        search?: string;
        exclude?: number[];
        include?: number[];
        orderby?: 'id' | 'count' | 'name' | 'slug';
        order?: 'asc' | 'desc';
        hide_empty?: boolean;
        parent?: number;
    }): Promise<WordPressCategory[]> {
        const queryParams = new URLSearchParams();

        // Valeurs par défaut
        queryParams.append('per_page', '100');
        queryParams.append('orderby', 'name');
        queryParams.append('order', 'asc');

        if (params) {
            if (params.per_page) queryParams.set('per_page', params.per_page.toString());
            if (params.page) queryParams.append('page', params.page.toString());
            if (params.search) queryParams.append('search', params.search);
            if (params.exclude && params.exclude.length > 0) {
                queryParams.append('exclude', params.exclude.join(','));
            }
            if (params.include && params.include.length > 0) {
                queryParams.append('include', params.include.join(','));
            }
            if (params.orderby) queryParams.set('orderby', params.orderby);
            if (params.order) queryParams.set('order', params.order);
            if (params.hide_empty !== undefined) {
                queryParams.append('hide_empty', params.hide_empty.toString());
            }
            if (params.parent !== undefined) {
                queryParams.append('parent', params.parent.toString());
            }
        }

        const url = `${this.baseUrl}/categories?${queryParams.toString()}`;
        return this.makeRequest<WordPressCategory[]>(url);
    }

    // Récupérer une catégorie spécifique
    async getCategory(id: number): Promise<WordPressCategory> {
        const url = `${this.baseUrl}/categories/${id}`;
        return this.makeRequest<WordPressCategory>(url);
    }

    // Récupérer les tags
    async getTags(params?: {
        per_page?: number;
        page?: number;
        search?: string;
        exclude?: number[];
        include?: number[];
        orderby?: 'id' | 'count' | 'name' | 'slug';
        order?: 'asc' | 'desc';
        hide_empty?: boolean;
    }): Promise<WordPressTag[]> {
        const queryParams = new URLSearchParams();

        // Valeurs par défaut
        queryParams.append('per_page', '100');
        queryParams.append('orderby', 'name');
        queryParams.append('order', 'asc');

        if (params) {
            if (params.per_page) queryParams.set('per_page', params.per_page.toString());
            if (params.page) queryParams.append('page', params.page.toString());
            if (params.search) queryParams.append('search', params.search);
            if (params.exclude && params.exclude.length > 0) {
                queryParams.append('exclude', params.exclude.join(','));
            }
            if (params.include && params.include.length > 0) {
                queryParams.append('include', params.include.join(','));
            }
            if (params.orderby) queryParams.set('orderby', params.orderby);
            if (params.order) queryParams.set('order', params.order);
            if (params.hide_empty !== undefined) {
                queryParams.append('hide_empty', params.hide_empty.toString());
            }
        }

        const url = `${this.baseUrl}/tags?${queryParams.toString()}`;
        return this.makeRequest<WordPressTag[]>(url);
    }

    // Récupérer les auteurs
    async getAuthors(params?: {
        per_page?: number;
        page?: number;
        search?: string;
        exclude?: number[];
        include?: number[];
        orderby?: 'id' | 'name' | 'slug' | 'registered_date';
        order?: 'asc' | 'desc';
    }): Promise<WordPressAuthor[]> {
        const queryParams = new URLSearchParams();

        // Valeurs par défaut
        queryParams.append('per_page', '100');
        queryParams.append('orderby', 'name');
        queryParams.append('order', 'asc');

        if (params) {
            if (params.per_page) queryParams.set('per_page', params.per_page.toString());
            if (params.page) queryParams.append('page', params.page.toString());
            if (params.search) queryParams.append('search', params.search);
            if (params.exclude && params.exclude.length > 0) {
                queryParams.append('exclude', params.exclude.join(','));
            }
            if (params.include && params.include.length > 0) {
                queryParams.append('include', params.include.join(','));
            }
            if (params.orderby) queryParams.set('orderby', params.orderby);
            if (params.order) queryParams.set('order', params.order);
        }

        const url = `${this.baseUrl}/users?${queryParams.toString()}`;
        return this.makeRequest<WordPressAuthor[]>(url);
    }

    // Récupérer un auteur spécifique
    async getAuthor(id: number): Promise<WordPressAuthor> {
        const url = `${this.baseUrl}/users/${id}`;
        return this.makeRequest<WordPressAuthor>(url);
    }

    // Recherche globale
    async search(query: string, params?: {
        type?: 'post' | 'page' | 'attachment';
        subtype?: string;
        per_page?: number;
        page?: number;
    }): Promise<any[]> {
        const queryParams = new URLSearchParams();
        queryParams.append('search', query);

        // Valeurs par défaut
        queryParams.append('type', 'post');
        queryParams.append('per_page', '20');

        if (params) {
            if (params.type) queryParams.set('type', params.type);
            if (params.subtype) queryParams.append('subtype', params.subtype);
            if (params.per_page) queryParams.set('per_page', params.per_page.toString());
            if (params.page) queryParams.append('page', params.page.toString());
        }

        const url = `${this.baseUrl}/search?${queryParams.toString()}`;
        return this.makeRequest<any[]>(url);
    }

    // Vérifier la santé de l'API
    async checkApiHealth(): Promise<boolean> {
        try {
            const response = await fetch(`${this.baseUrl.replace('/wp/v2', '')}`);
            return response.ok;
        } catch (error) {
            console.error('API WordPress non accessible:', error);
            return false;
        }
    }

    // Obtenir les statistiques du site
    async getSiteStats(): Promise<{
        posts: number;
        categories: number;
        tags: number;
        authors: number;
    } | null> {
        try {
            const [posts, categories, tags, authors] = await Promise.all([
                this.getPosts({ per_page: 1 }),
                this.getCategories({ per_page: 1 }),
                this.getTags({ per_page: 1 }),
                this.getAuthors({ per_page: 1 })
            ]);

            return {
                posts: posts.length,
                categories: categories.length,
                tags: tags.length,
                authors: authors.length
            };
        } catch (error) {
            console.error('Erreur lors de la récupération des statistiques:', error);
            return null;
        }
    }

    // Fonction utilitaire pour nettoyer le contenu HTML
    stripHtml(html: string): string {
        if (!html) return '';
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    }

    // Fonction utilitaire pour formater la date
    formatDate(dateString: string, locale: string = 'fr-FR'): string {
        const date = new Date(dateString);
        return date.toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    // Fonction utilitaire pour formater la date avec l'heure
    formatDateTime(dateString: string, locale: string = 'fr-FR'): string {
        const date = new Date(dateString);
        return date.toLocaleString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    // Fonction utilitaire pour obtenir un extrait de texte
    getExcerpt(content: string, wordLimit: number = 25): string {
        const plainText = this.stripHtml(content);
        const words = plainText.split(' ');
        if (words.length <= wordLimit) return plainText;
        return words.slice(0, wordLimit).join(' ') + '...';
    }

    // Fonction utilitaire pour estimer le temps de lecture
    getReadingTime(content: string): number {
        const plainText = this.stripHtml(content);
        const wordCount = plainText.split(' ').length;
        const wordsPerMinute = 200; // Moyenne de lecture
        return Math.ceil(wordCount / wordsPerMinute);
    }

    // Fonction utilitaire pour générer un slug à partir du titre
    generateSlug(title: string): string {
        return title
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
            .replace(/[^a-z0-9\s-]/g, '') // Garder seulement lettres, chiffres, espaces et tirets
            .replace(/\s+/g, '-') // Remplacer espaces par tirets
            .replace(/-+/g, '-') // Supprimer tirets multiples
            .replace(/^-|-$/g, '') // Supprimer tirets en début/fin
            .trim();
    }

    // Nettoyer le cache
    clearCache(): void {
        this.cache.clear();
    }

    // Définir le timeout du cache
    setCacheTimeout(timeout: number): void {
        this.cacheTimeout = timeout;
    }
}

export const wordpressApi = new WordPressApiService();
